import { queryClient } from '@/lib/react-query';
import {
  GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY,
  TGetCustomerProfileFromCaseResponse,
} from '@/features/cases/case/api/get-customer-profile-from-case.api';
import { SINGLE_CASE_QUERY_KEY, TSingleCaseResponse } from '@/features/cases/case/api/get-single-case.api';
import { GET_DETAIL_CUSTOMER_QUERY } from '@/features/customers/customer/api/get-detail-customer.api';
import { TAssignLabelCustomerValidation } from '../../../api/assign-label-customer.api';
import { TLabelCustomer } from '../../../types/label-customer.type';
import { AxiosErrorCustom } from '@/lib/axios';
import { axiosExtractErrorMessage } from '@/lib/axios';

export type TAssignLabelCustomerOptimisticContext = {
  previousCustomerProfileData?: TGetCustomerProfileFromCaseResponse;
  previousSingleCaseData?: TSingleCaseResponse;
};

export interface IAssignLabelCustomerOptimisticUpdateParams {
  caseId?: string;
  customerId?: string;
  allAvailableLabels: TLabelCustomer[];
}

/**
 * Optimistic update handler for assign label customer mutation
 * Updates the customer profile cache immediately when labels are assigned
 */
export const createAssignLabelCustomerOptimisticUpdate = ({
  caseId,
  customerId,
  allAvailableLabels,
}: IAssignLabelCustomerOptimisticUpdateParams) => {
  return {
    onMutate: async (variables: TAssignLabelCustomerValidation): Promise<TAssignLabelCustomerOptimisticContext> => {
      // Map labelIds to full label objects (reusable for both queries)
      const updatedLabelCustomers = variables.labelIds
        .map((labelId: string) => {
          const label = allAvailableLabels.find((l: TLabelCustomer) => l.labelCustomerId === labelId);
          return label
            ? {
                labelCustomerId: label.labelCustomerId,
                name: label.name,
                color: label.color,
              }
            : null;
        })
        .filter((label): label is { labelCustomerId: string; name: string; color: string } => label !== null);

      const context: TAssignLabelCustomerOptimisticContext = {};

      // Cancel any outgoing refetches to avoid overwriting optimistic update
      if (caseId) {
        // Cancel and snapshot customer profile from case query
        await queryClient.cancelQueries({
          queryKey: [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId],
        });

        const previousCustomerProfileData = queryClient.getQueryData<TGetCustomerProfileFromCaseResponse>([
          GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY,
          caseId,
        ]);

        context.previousCustomerProfileData = previousCustomerProfileData;

        // Optimistically update customer profile from case cache
        if (previousCustomerProfileData?.payload) {
          queryClient.setQueryData<TGetCustomerProfileFromCaseResponse>(
            [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId],
            (old) => {
              if (!old?.payload) return old;
              return {
                ...old,
                payload: {
                  ...old.payload,
                  customer: {
                    ...old.payload.customer,
                    labelCustomers: updatedLabelCustomers,
                  },
                },
              };
            }
          );
        }

        // Cancel and snapshot single case query
        await queryClient.cancelQueries({
          queryKey: [SINGLE_CASE_QUERY_KEY, { caseId }],
        });

        const previousSingleCaseData = queryClient.getQueryData<TSingleCaseResponse>([
          SINGLE_CASE_QUERY_KEY,
          { caseId },
        ]);

        context.previousSingleCaseData = previousSingleCaseData;

        // Optimistically update single case cache
        if (previousSingleCaseData?.payload) {
          queryClient.setQueryData<TSingleCaseResponse>([SINGLE_CASE_QUERY_KEY, { caseId }], (old) => {
            if (!old?.payload) return old;
            return {
              ...old,
              payload: {
                ...old.payload,
                customer: {
                  ...old.payload.customer,
                  labelCustomers: updatedLabelCustomers,
                },
              },
            };
          });
        }
      }

      return context;
    },

    onError: (
      error: AxiosErrorCustom,
      _variables: TAssignLabelCustomerValidation,
      context: TAssignLabelCustomerOptimisticContext | undefined
    ) => {
      // Rollback to previous data on error
      if (caseId) {
        if (context?.previousCustomerProfileData) {
          queryClient.setQueryData(
            [GET_CUSTOMER_PROFILE_FROM_CASE_QUERY_KEY, caseId],
            context.previousCustomerProfileData
          );
        }

        if (context?.previousSingleCaseData) {
          queryClient.setQueryData([SINGLE_CASE_QUERY_KEY, { caseId }], context.previousSingleCaseData);
        }
      }

      return {
        errorMessage: axiosExtractErrorMessage(error) || 'Failed to assign customer labels',
      };
    },

    onSuccess: () => {
      // Invalidate other queries that might be affected
      if (customerId) {
        queryClient.invalidateQueries({
          queryKey: [GET_DETAIL_CUSTOMER_QUERY, { customerId }],
        });
      }
      // Don't invalidate the case queries immediately - let the optimistic update stay visible
      // The data will sync on the next natural refetch or when the component remounts
      // This prevents the "delayed" feeling by keeping the optimistic update visible
    },
  };
};
